/**
 * Granbay official logo — global lockup.
 *
 * Construction lifted from the v2 demo (founder direction 2026-05-24):
 *   - Square tile, 8px radius, dark in light theme / light in dark theme
 *   - SVG <text> "G" rendered Helvetica 900 with 5pt stroke so the
 *     glyph reads chunky at small sizes
 *   - Wordmark in Geist (var(--font-sans)), 20px font-weight 800,
 *     letter-spacing -0.04em
 *
 * Wired up to v6 tokens so the same component theme-flips: the tile
 * pins to a fixed dark mark in both themes (Vercel / Stripe / Linear
 * brand-mark pattern) — black tile + white "G" on light, near-white
 * tile + black "G" on dark — and the wordmark uses var(--color-text).
 */
"use client";

export type GranbayLogoVariant = "icon" | "lockup" | "wordmark";

export interface GranbayLogoProps {
  size?: number;
  variant?: GranbayLogoVariant;
  className?: string;
  ariaLabel?: string;
}

export function GranbayLogo({
  size = 36,
  variant = "lockup",
  className,
  ariaLabel = "Granbay",
}: GranbayLogoProps) {
  const iconBox = (
    <span
      aria-label={variant === "icon" ? ariaLabel : undefined}
      role={variant === "icon" ? "img" : undefined}
      className="granbay-logo-tile"
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        style={{ width: "72%", height: "72%" }}
        aria-hidden="true"
      >
        <text
          className="granbay-logo-glyph"
          x="50"
          y="81"
          textAnchor="middle"
          fontFamily="Helvetica, Arial, sans-serif"
          fontSize="101"
          fontWeight="900"
          strokeWidth="5"
          strokeLinejoin="round"
        >
          G
        </text>
      </svg>
    </span>
  );

  if (variant === "icon") return <span className={className}>{iconBox}</span>;

  const wordmark = (
    <span
      style={{
        fontFamily: "var(--font-sans)",
        fontSize: variant === "wordmark" ? size : 20,
        fontWeight: 800,
        color: "var(--color-text)",
        letterSpacing: "-0.04em",
        lineHeight: 1,
      }}
    >
      Granbay
    </span>
  );

  if (variant === "wordmark") {
    return (
      <span className={className} role="img" aria-label={ariaLabel}>
        {wordmark}
      </span>
    );
  }

  return (
    <span
      className={className}
      role="img"
      aria-label={ariaLabel}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      {iconBox}
      {wordmark}
    </span>
  );
}
